import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSocket } from "@/providers/socket-provider";
import { useAuth } from "@/providers/auth-provider";
import { showMessageBox } from "@/components/blocks/message-box";

export const PLAY_REQUEST = {
  LOBBY_SUBSCRIBE: "LOBBY_SUBSCRIBE",
  LOBBY_UNSUBSCRIBE: "LOBBY_UNSUBSCRIBE",
  GAME_ROOM_LIST: "GAME_ROOM_LIST",
  GAME_CREATE_ROOM: "GAME_CREATE_ROOM",
  GAME_JOIN_ROOM: "GAME_JOIN_ROOM",
  GAME_LEAVE_ROOM: "GAME_LEAVE_ROOM",
  GAME_START: "GAME_START",
  GAME_ACTION: "GAME_ACTION",
  GAME_STATE: "GAME_STATE",
} as const;

export const PLAY_RESPONSE = [
  "LOBBY_SUBSCRIBED",
  "LOBBY_UNSUBSCRIBED",
  "GAME_ROOM_LIST",
  "GAME_ROOM_DELETED",
  "GAME_ROOM_CREATED",
  "GAME_ROOM_UPDATED",
  "GAME_ROOM_JOINED",
  "GAME_ROOM_LEFT",
  "PLAYER_JOINED",
  "PLAYER_LEFT",
  "GAME_STARTED",
  "GAME_ACTION",
  "GAME_STATE",
] as const;

interface GameRoomSummary {
  roomId: string;
  gameType: string;
  roomTitle: string;
  hostName: string;
  currentPlayers: number;
  maxPlayers: number;
  status: string;
  createdAt: number;
  hasPassword: boolean;
  playerNames: string[];
}

export interface GameRoom {
  roomId: string;
  gameType: string;
  roomTitle: string;
  hostId: string;
  hostName: string;
  players: Array<{ id: string; name: string }>;
  maxPlayers: number;
  status: "waiting" | "playing" | "finished";
  gameState?: any;
}

interface PlayContextType {
  isLobbySubscribed: boolean;
  gameRooms: GameRoomSummary[];
  currentRoom: GameRoom | null;
  subscribeLobby: () => void;
  unsubscribeLobby: () => void;
  getRoomList: (gameType?: string) => void;
  createGameRoom: (
    gameType: string,
    maxPlayers?: number,
    password?: string,
    roomTitle?: string,
  ) => void;
  joinGameRoom: (roomId: string, password?: string) => void;
  leaveGameRoom: () => void;
  startGame: () => void;
  sendGameAction: (action: string, actionData: any) => void;
  sendGameState: (gameState: any) => void;
}

const PlayContext = createContext<PlayContextType | undefined>(undefined);

export function PlayProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const {
    isConnected,
    sendMessage,
    registerMessageHandler,
    unregisterMessageHandler,
  } = useSocket();

  const [isLobbySubscribed, setIsLobbySubscribed] = useState(false);
  const [gameRooms, setGameRooms] = useState<GameRoomSummary[]>([]);
  const [currentRoom, setCurrentRoom] = useState<GameRoom | null>(null);

  // 로비 구독
  const subscribeLobby = useCallback(() => {
    if (isConnected) {
      sendMessage({ type: PLAY_REQUEST.LOBBY_SUBSCRIBE });
    }
  }, [isConnected, sendMessage]);

  // 로비 구독 해제
  const unsubscribeLobby = useCallback(() => {
    if (isConnected) {
      sendMessage({ type: PLAY_REQUEST.LOBBY_UNSUBSCRIBE });
    }
  }, [isConnected, sendMessage]);

  // 게임룸 목록 조회
  const getRoomList = useCallback(
    (gameType?: string) => {
      if (isConnected) {
        sendMessage({
          type: PLAY_REQUEST.GAME_ROOM_LIST,
          ...(gameType && { gameType }),
        });
      }
    },
    [isConnected, sendMessage],
  );

  // 게임룸 생성
  const createGameRoom = useCallback(
    (
      gameType: string,
      maxPlayers = 4,
      password?: string,
      roomTitle?: string,
    ) => {
      if (isConnected) {
        sendMessage({
          type: PLAY_REQUEST.GAME_CREATE_ROOM,
          gameType,
          maxPlayers,
          ...(password && { password }),
          ...(roomTitle && { roomTitle }),
        });
      }
    },
    [isConnected, sendMessage],
  );

  // 게임룸 참여
  const joinGameRoom = useCallback(
    (roomId: string, password?: string) => {
      if (isConnected) {
        sendMessage({
          type: PLAY_REQUEST.GAME_JOIN_ROOM,
          roomId,
          ...(password && { password }),
        });
      }
    },
    [isConnected, sendMessage],
  );

  // 게임룸 나가기
  const leaveGameRoom = useCallback(() => {
    if (isConnected) {
      sendMessage({ type: PLAY_REQUEST.GAME_LEAVE_ROOM });
    }
  }, [isConnected, sendMessage]);

  // 게임 시작
  const startGame = useCallback(() => {
    if (isConnected && currentRoom?.hostId === user?._id) {
      sendMessage({ type: PLAY_REQUEST.GAME_START });
    }
  }, [isConnected, sendMessage, currentRoom, user]);

  // 게임 액션 전송
  const sendGameAction = useCallback(
    (action: string, actionData: any) => {
      if (isConnected && currentRoom) {
        sendMessage({
          type: PLAY_REQUEST.GAME_ACTION,
          action,
          actionData,
        });
      }
    },
    [isConnected, sendMessage, currentRoom],
  );

  // 게임 상태 전송
  const sendGameState = useCallback(
    (gameState: any) => {
      if (isConnected && currentRoom) {
        sendMessage({
          type: PLAY_REQUEST.GAME_STATE,
          gameState,
        });
      }
    },
    [isConnected, sendMessage, currentRoom],
  );

  // 메시지 핸들러들
  const handleLobbySubscribed = useCallback(async () => {
    console.log("[PLAY] Lobby subscribed");
    setIsLobbySubscribed(true);
  }, []);

  const handleLobbyUnsubscribed = useCallback(async () => {
    console.log("[PLAY] Lobby unsubscribed");
    setIsLobbySubscribed(false);
  }, []);

  const handleGameRoomList = useCallback(async (data: any) => {
    console.log("[PLAY] Game room list:", data);
    setGameRooms(data.rooms || []);
  }, []);

  const handleGameRoomDeleted = useCallback(async (data: any) => {
    console.log("[PLAY] Game room deleted:", data);
    setGameRooms((prev) => prev.filter((room) => room.roomId !== data.roomId));
  }, []);

  const handleGameRoomCreated = useCallback(async (data: any) => {
    console.log("[PLAY] Game room created:", data);
    // 로비용 데이터이므로 currentRoom 설정하지 않음
    // 대신 gameRooms 목록에 새 룸 추가 (필요시)
    if (data.room && !data.room.players) {
      // 로비용 데이터인 경우 gameRooms 업데이트
      setGameRooms((prev) => {
        const exists = prev.find((room) => room.roomId === data.room.roomId);
        if (!exists) {
          return [...prev, data.room];
        }
        return prev;
      });
    }
  }, []);

  const handleGameRoomUpdated = useCallback(async (data: any) => {
    console.log("[PLAY] Game room updated:", data);
    if (data.room && !data.room.players) {
      // 로비용 데이터인 경우 gameRooms 업데이트
      setGameRooms((prev) => {
        const updated = prev.map((room) =>
          room.roomId === data.room.roomId ? data.room : room,
        );
        // 새로운 방이면 추가
        if (!prev.find((room) => room.roomId === data.room.roomId)) {
          return [...prev, data.room];
        }
        return updated;
      });
    }
  }, []);

  const handleGameRoomJoined = useCallback(async (data: any) => {
    // 게임룸에 참여한 경우 currentRoom 설정.
    console.log("[PLAY] Game room joined:", data);
    setCurrentRoom(data.room);
  }, []);

  const handleGameRoomLeft = useCallback(async (data: any) => {
    // 게임룸에서 나간 경우 currentRoom 초기화.
    console.log("[PLAY] Game room left:", data);
    setCurrentRoom(null);
    showMessageBox({
      type: "Info",
      title: "게임룸 나가기",
      description: data.message || "게임룸에서 나갔습니다.",
    });
  }, []);

  const handlePlayerJoined = useCallback(
    // 플레이어가 게임룸에 참여한 경우 currentRoom 업데이트.
    async (data: any) => {
      console.log("[PLAY] Player joined:", data);
      if (currentRoom) {
        setCurrentRoom((prev) =>
          prev ? { ...prev, players: data.players } : null,
        );
      }
    },
    [currentRoom],
  );

  const handlePlayerLeft = useCallback(
    // 플레이어가 게임룸에서 나간 경우 currentRoom 업데이트.
    async (data: any) => {
      console.log("[PLAY] Player left:", data);
      if (currentRoom) {
        setCurrentRoom((prev) =>
          prev
            ? {
                ...prev,
                players: data.players,
                ...(data.newHostId && { hostId: data.newHostId }),
                ...(data.newHostName && { hostName: data.newHostName }),
              }
            : null,
        );
      }
    },
    [currentRoom],
  );

  const handleGameStarted = useCallback(
    // 게임이 시작된 경우 currentRoom 업데이트.
    async (data: any) => {
      console.log("[PLAY] Game started:", data);
      if (currentRoom) {
        setCurrentRoom((prev) =>
          prev
            ? {
                ...prev,
                status: data.status,
                gameState: data.gameState,
              }
            : null,
        );
      }
    },
    [currentRoom],
  );

  const handleGameAction = useCallback(async (data: any) => {
    console.log("[PLAY] Game action:", data);

    // 게임에 액션 전달하는 이벤트 발생
    const gameActionEvent = new CustomEvent("onlineGameAction", {
      detail: data,
    });
    window.dispatchEvent(gameActionEvent);
  }, []);

  const handleGameState = useCallback(
    async (data: any) => {
      console.log("[PLAY] Game state:", data);
      if (currentRoom) {
        setCurrentRoom((prev) =>
          prev
            ? {
                ...prev,
                gameState: data.gameState,
              }
            : null,
        );

        // 게임에 상태 업데이트 전달
        const gameStateEvent = new CustomEvent("onlineGameStateUpdate", {
          detail: data,
        });
        window.dispatchEvent(gameStateEvent);
      }
    },
    [currentRoom],
  );

  // 이벤트 핸들러 등록
  useEffect(() => {
    const handlers = {
      LOBBY_SUBSCRIBED: handleLobbySubscribed,
      LOBBY_UNSUBSCRIBED: handleLobbyUnsubscribed,
      GAME_ROOM_LIST: handleGameRoomList,
      GAME_ROOM_DELETED: handleGameRoomDeleted,
      GAME_ROOM_CREATED: handleGameRoomCreated,
      GAME_ROOM_UPDATED: handleGameRoomUpdated,
      GAME_ROOM_JOINED: handleGameRoomJoined,
      GAME_ROOM_LEFT: handleGameRoomLeft,
      PLAYER_JOINED: handlePlayerJoined,
      PLAYER_LEFT: handlePlayerLeft,
      GAME_STARTED: handleGameStarted,
      GAME_ACTION: handleGameAction,
      GAME_STATE: handleGameState,
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      registerMessageHandler(event, handler);
    });

    return () => {
      Object.keys(handlers).forEach((event) => {
        unregisterMessageHandler(event);
      });
    };
  }, [
    handleLobbySubscribed,
    handleLobbyUnsubscribed,
    handleGameRoomList,
    handleGameRoomDeleted,
    handleGameRoomCreated,
    handleGameRoomUpdated,
    handleGameRoomJoined,
    handleGameRoomLeft,
    handlePlayerJoined,
    handlePlayerLeft,
    handleGameStarted,
    handleGameAction,
    handleGameState,
    registerMessageHandler,
    unregisterMessageHandler,
  ]);

  const value: PlayContextType = {
    isLobbySubscribed,
    gameRooms,
    currentRoom,
    subscribeLobby,
    unsubscribeLobby,
    getRoomList,
    createGameRoom,
    joinGameRoom,
    leaveGameRoom,
    startGame,
    sendGameAction,
    sendGameState,
  };

  return <PlayContext.Provider value={value}>{children}</PlayContext.Provider>;
}

export const usePlay = () => {
  const context = useContext(PlayContext);
  if (!context) {
    throw new Error("usePlay must be used within a PlayProvider");
  }
  return context;
};
